import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { botService } from '../services/botService'
import { Bot, BotCreateRequest, BotUpdateRequest } from '../types/bot.types'

interface BotState {
  bots: Bot[]
  currentBot: Bot | null
  isLoading: boolean
  error: string | null
}

const initialState: BotState = {
  bots: [],
  currentBot: null,
  isLoading: false,
  error: null,
}

// Async thunks
export const fetchBots = createAsyncThunk(
  'bot/fetchBots',
  async (_, { rejectWithValue }) => {
    try {
      const response = await botService.getBots()
      return response.data.bots
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erro ao carregar bots')
    }
  }
)

export const fetchBot = createAsyncThunk(
  'bot/fetchBot',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await botService.getBot(id)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erro ao carregar bot')
    }
  }
)

export const createBot = createAsyncThunk(
  'bot/createBot',
  async (botData: BotCreateRequest, { rejectWithValue }) => {
    try {
      const response = await botService.createBot(botData)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erro ao criar bot')
    }
  }
)

export const updateBot = createAsyncThunk(
  'bot/updateBot',
  async ({ id, data }: { id: number; data: BotUpdateRequest }, { rejectWithValue }) => {
    try {
      const response = await botService.updateBot(id, data)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erro ao atualizar bot')
    }
  }
)

export const deleteBot = createAsyncThunk(
  'bot/deleteBot',
  async (id: number, { rejectWithValue }) => {
    try {
      await botService.deleteBot(id)
      return id
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erro ao deletar bot')
    }
  }
)

export const connectBot = createAsyncThunk(
  'bot/connectBot',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await botService.connectBot(id)
      return response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erro ao conectar bot')
    }
  }
)

export const disconnectBot = createAsyncThunk(
  'bot/disconnectBot',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await botService.disconnectBot(id)
      return response.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Erro ao desconectar bot')
    }
  }
)

const botSlice = createSlice({
  name: 'bot',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setCurrentBot: (state, action: PayloadAction<Bot | null>) => {
      state.currentBot = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Bots
      .addCase(fetchBots.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchBots.fulfilled, (state, action) => {
        state.isLoading = false
        state.bots = action.payload
      })
      .addCase(fetchBots.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      
      // Fetch Bot
      .addCase(fetchBot.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchBot.fulfilled, (state, action) => {
        state.isLoading = false
        state.currentBot = action.payload
      })
      .addCase(fetchBot.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      
      // Create Bot
      .addCase(createBot.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(createBot.fulfilled, (state, action) => {
        state.isLoading = false
        state.bots.push(action.payload)
      })
      .addCase(createBot.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      
      // Update Bot
      .addCase(updateBot.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(updateBot.fulfilled, (state, action) => {
        state.isLoading = false
        const index = state.bots.findIndex(bot => bot.id === action.payload.id)
        if (index !== -1) {
          state.bots[index] = action.payload
        }
        if (state.currentBot?.id === action.payload.id) {
          state.currentBot = action.payload
        }
      })
      .addCase(updateBot.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      
      // Delete Bot
      .addCase(deleteBot.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(deleteBot.fulfilled, (state, action) => {
        state.isLoading = false
        state.bots = state.bots.filter(bot => bot.id !== action.payload)
        if (state.currentBot?.id === action.payload) {
          state.currentBot = null
        }
      })
      .addCase(deleteBot.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      
      // Connect Bot
      .addCase(connectBot.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(connectBot.fulfilled, (state) => {
        state.isLoading = false
        // O connectBot não retorna dados específicos do bot
        // O status será atualizado via polling
      })
      .addCase(connectBot.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      
      // Disconnect Bot
      .addCase(disconnectBot.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(disconnectBot.fulfilled, (state, action) => {
        state.isLoading = false
        // Atualizar status do bot
        const bot = state.bots.find(b => b.id === action.payload.id)
        if (bot) {
          bot.status = 'disconnected'
          bot.qr_code = undefined
        }
      })
      .addCase(disconnectBot.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
  },
})

export const { clearError, setCurrentBot } = botSlice.actions
export default botSlice.reducer

